import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Radio, ArrowRight, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ProspectPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/tenant/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Tenant not found"));
  }, [slug]);

  if (err) return <div className="p-12 text-center text-zinc-500">{err}</div>;
  if (!data) return <div className="p-12 text-center text-zinc-500">Loading…</div>;

  const { tenant, channels, epg, call_to_action } = data;
  const primary = tenant.branding?.primary_color || "#EF4444";

  return (
    <div className="min-h-screen bg-black text-zinc-100">
      <header className="border-b border-zinc-900 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center font-display font-black" style={{ background: primary }}>
            {tenant.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="font-display font-black text-lg">{tenant.name}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              powered by MOXYPLAY · plan {tenant.plan}
            </div>
          </div>
        </div>
        <Link to="/signup" data-testid="prospect-signup-btn" className="rounded-sm px-4 py-2 text-sm font-mono uppercase tracking-widest text-white" style={{ background: primary }}>
          {call_to_action.trial_button} <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
        </Link>
      </header>

      <section className="px-6 py-16 border-b border-zinc-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-400 mb-3">
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1" /> LIVE · ATSC A/85 CALM Act compliant
          </div>
          <h1 className="font-display font-black text-5xl lg:text-6xl leading-tight mb-4" data-testid="prospect-headline">
            {call_to_action.headline}
          </h1>
          <p className="text-lg text-zinc-400 mb-8" data-testid="prospect-sub">{call_to_action.sub}</p>
          <div className="flex gap-3">
            <Link to="/signup" data-testid="prospect-cta-primary" className="rounded-sm px-5 py-3 font-mono uppercase tracking-widest text-white" style={{ background: primary }}>
              {call_to_action.trial_button}
            </Link>
            <a href={`${BACKEND}/api/public/xmltv/${tenant.slug}?token=disabled`} className="rounded-sm px-5 py-3 font-mono uppercase tracking-widest border border-zinc-700 text-zinc-300">
              XMLTV feed
            </a>
          </div>
        </div>
      </section>

      <section className="px-6 py-12 border-b border-zinc-900">
        <div className="max-w-5xl mx-auto">
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-3">§ Live channel wall</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="prospect-channel-wall">
            {channels.map((c) => (
              <div
                key={c.id}
                className="border border-zinc-800 rounded-sm p-4 bg-zinc-950 hover:border-zinc-600 transition-colors"
                data-testid={`prospect-channel-${c.slug}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-2 h-2 rounded-sm" style={{ background: c.color }} />
                  <span className={`font-mono text-[10px] uppercase tracking-widest ${c.status === "on_air" ? "text-red-400" : "text-zinc-500"}`}>
                    {c.status === "on_air" ? "● LIVE" : c.status}
                  </span>
                  <span className="ml-auto font-mono text-[10px] text-zinc-500">{c.resolution}</span>
                </div>
                <div className="font-display font-black text-2xl" style={{ color: c.color }}>{c.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-2">
                  {c.audio_profile?.primary_layout || "5.1"} · {c.audio_profile?.delivery_codec || "AC-3"} · target {c.audio_profile?.loudness_target_lkfs ?? -24} LKFS
                </div>
                {c.hdr_mode && c.hdr_mode !== "SDR" && (
                  <div className="mt-2 inline-block text-[9px] font-mono uppercase tracking-widest border border-amber-500/60 text-amber-300 px-1.5 py-0.5 rounded-sm">
                    {c.hdr_mode}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {epg && epg.length > 0 && (
        <section className="px-6 py-12 border-b border-zinc-900">
          <div className="max-w-5xl mx-auto">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-3">§ Airing right now</div>
            <div className="grid md:grid-cols-2 gap-3" data-testid="prospect-epg">
              {epg.map((row) => (
                <div key={row.slug} className="border border-zinc-800 rounded-sm p-4 bg-zinc-950">
                  <div className="font-display font-bold mb-2">{row.channel}</div>
                  {(row.programs || []).length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">—</div>
                  ) : row.programs.map((p, i) => (
                    <div key={i} className="text-xs mb-1">
                      <span className="text-zinc-500 font-mono">{new Date(p.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                      <span className="ml-2 text-zinc-100">{p.title}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="px-6 py-8 text-center">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">
          <Radio className="w-3 h-3 inline mr-1" /> MOXYPLAY — cloud-based playout for FAST channels
        </div>
      </footer>
    </div>
  );
}
